import { useState, useEffect, useRef } from "react";
import Post from "./Post";
import "./Feed.css";
import { FaUser, FaCamera, FaLink, FaVideo, FaBriefcase, FaArrowRight, FaRegEye, FaUserPlus, FaCompass, FaChartLine, FaTimes, FaImage, FaCloudUploadAlt, FaPaperPlane, FaFileAlt } from "react-icons/fa";
import { get, post } from "../http/api";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "./cropImage";
import SuggestedProfileCard from "./matching/SuggestedProfileCard";
import { fetchSuggestedProfiles } from "./matching/suggestedProfiles";
import SkeletonPost from "./SkeletonPost";
import OfferModal from "./OfferModal";
import { useTranslation } from "react-i18next";
import { getMediaUrl } from "../http/media";



export default function Feed({ onNavigate }) {

  const id_user = sessionStorage.getItem("id_user");

  const user = JSON.parse(sessionStorage.getItem("user"));

  const roleoffer = user?.role;

  const [posts, setPosts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);

  const [publishing, setPublishing] = useState(false);

  const [publishSuccess, setPublishSuccess] = useState(false);

  const [hasMore, setHasMore] = useState(true);

  const [loadingMore, setLoadingMore] = useState(false);
  const feedEndRef = useRef(null);
  const feedStreamRef = useRef(null);
  const activeRequestRef = useRef(null);
  const loadMoreRef = useRef(null);
  const impressionQueueRef = useRef(new Set());
  const impressionFlushTimerRef = useRef(null);
  const pullStartRef = useRef(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [pullRefreshing, setPullRefreshing] = useState(false);


  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");

  const [modalType, setModalType] = useState(null);
  const [legend, setLegend] = useState("");
  const [linkLegend, setLinkLegend] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [preview, setPreview] = useState(null);
  const [modalError, setModalError] = useState("");

  const profilePulse =
    (user?.username || user?.name ? 40 : 0) +
    (roleoffer ? 30 : 0) +
    (preview ? 30 : 0);

  const [suggestions, setSuggestions] = useState([]);
  const [weeklyProfileViews, setWeeklyProfileViews] = useState(null);

  const { t } = useTranslation();

  const getRoleLabel = (role) => {
    const normalizedRole = String(role || "").trim().toLowerCase();
    return normalizedRole
      ? t(`matchCard.${normalizedRole}`, { defaultValue: role })
      : "";
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    const data = await fetchSuggestedProfiles();
    setSuggestions(data);
  };

  useEffect(() => {
    const refreshWeeklyProfileViews = async () => {
      try {
        const res = await get(`profile/weekly-views?refresh=${Date.now()}`);

        if (res.message !== "DATA_SUCCESS") {
          throw new Error(res.message || "Unable to load profile views");
        }

        setWeeklyProfileViews(Number(res.data?.unique_visitors || 0));
      } catch (err) {
        console.error("Unable to load weekly profile views:", err);
      }
    };

    refreshWeeklyProfileViews();

    const intervalId = window.setInterval(refreshWeeklyProfileViews, 60000);
    window.addEventListener("focus", refreshWeeklyProfileViews);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWeeklyProfileViews);
    };
  }, []);

  const fetchPhoto = async () => {
    try {
      const res = await get("profile/photo");

      if (res.data?.photo_url) {
        setPreview(
          // `${API_URL}${res.data.photo_url}?t=${Date.now()}`
          `${getMediaUrl(res.data.photo_url)}?t=${Date.now()}`
        );
      } else {
        setPreview(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
    }
  };

  useEffect(() => {
    fetchPhoto();
  }, []);



  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.2);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [errorVideo, setErrorVideo] = useState();


  const [videoPreview, setVideoPreview] = useState(null);
  const [videoFile, setVideoFile] = useState(null);


  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {

      if (video.duration > 60) {
        setErrorVideo(t("feed.videoLimit"));
        setTimeout(() => {

          setErrorVideo("");

        }, 2500);
        return;
      }

      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    };

    video.src = URL.createObjectURL(file);
  };


  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const openComposer = (event) => {
      const type = event?.detail?.type || "post";
      if (type === "text") {
        setModalType("text");
        return;
      }
      setModalType(type);
    };
    window.addEventListener("carrerup:open-create-post", openComposer);
    return () => window.removeEventListener("carrerup:open-create-post", openComposer);
  }, []);

  const fetchPosts =
    async (
      cursor = null
    ) => {
      const requestKey = cursor || "__first_page__";
      if (activeRequestRef.current === requestKey) return;
      activeRequestRef.current = requestKey;

      try {
        if (!cursor) setLoading(true);

        const endpoint =
          cursor
            ?
            `posts?cursor=${cursor}`
            :
            `posts`;
        const res = await get(endpoint);

        const data = res.data;

        const hasContent = (value) => {
          if (value === null || value === undefined) return false;
          const normalized = String(value).trim().toLowerCase();
          return normalized !== "" && !["null", "undefined", "false"].includes(normalized);
        };
        const incomingPosts = Array.isArray(data.posts)
          ? data.posts.filter((item) =>
              hasContent(item?.text) ||
              hasContent(item?.image) ||
              hasContent(item?.video) ||
              hasContent(item?.link_url)
            )
          : [];
        setPosts((previousPosts) => {
          const combined = cursor
            ? [...previousPosts, ...incomingPosts]
            : incomingPosts;
          const uniquePosts = new Map();

          combined.forEach((item) => {
            if (item?.id_post != null) {
              uniquePosts.set(String(item.id_post), item);
            }
          });

          return Array.from(uniquePosts.values());
        });
        setNextCursor(
          data.nextCursor
        );
        setHasMore(
          data.hasMore
        );
      }
      catch (err) {

        console.log(
          err
        );

      }
      finally {
        if (!cursor) setLoading(false);
        if (activeRequestRef.current === requestKey) {
          activeRequestRef.current = null;
        }
      }
    };

  const loadMorePosts =
    async () => {

      if (
        loadingMore ||
        !hasMore ||
        !nextCursor
      ) {

        return;

      }

      try {

        setLoadingMore(
          true
        );

        await fetchPosts(
          nextCursor
        );

      }
      finally {

        setLoadingMore(
          false
        );

      }

    };

  const handlePullStart = (event) => {
    if (!document.documentElement.classList.contains("native-app") || window.scrollY > 2) return;
    pullStartRef.current = event.touches?.[0]?.clientY ?? null;
  };

  const handlePullMove = (event) => {
    if (pullStartRef.current == null || window.scrollY > 2) return;
    const currentY = event.touches?.[0]?.clientY ?? pullStartRef.current;
    setPullDistance(Math.min(92, Math.max(0, (currentY - pullStartRef.current) * 0.45)));
  };

  const handlePullEnd = async () => {
    const shouldRefresh = pullDistance >= 58;
    pullStartRef.current = null;
    if (!shouldRefresh) {
      setPullDistance(0);
      return;
    }
    setPullRefreshing(true);
    setPullDistance(52);
    await fetchPosts();
    setPullRefreshing(false);
    setPullDistance(0);
  };

  loadMoreRef.current = loadMorePosts;

  useEffect(() => {
    const sentinel = feedEndRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMoreRef.current?.();
      },
      {
        root: null,
        rootMargin: "1600px 0px",
        threshold: 0
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextCursor, hasMore, loadingMore]);

  useEffect(() => {
    const container = feedStreamRef.current;
    if (!container || posts.length === 0) return undefined;

    const visibilityTimers = new Map();

    const flushImpressions = async () => {
      impressionFlushTimerRef.current = null;
      const postIds = Array.from(impressionQueueRef.current);
      impressionQueueRef.current.clear();

      if (postIds.length === 0) return;

      try {
        await post("posts/impressions", { postIds });
      } catch (error) {
        // A failed analytics write must never interrupt the feed.
        postIds.forEach((postId) => impressionQueueRef.current.add(postId));
        console.warn("Unable to record post impressions:", error);
      }
    };

    const queueImpression = (postId) => {
      impressionQueueRef.current.add(postId);
      if (!impressionFlushTimerRef.current) {
        impressionFlushTimerRef.current = window.setTimeout(
          flushImpressions,
          1200
        );
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const postId = Number(entry.target.dataset.feedPostId);
          if (!postId || entry.target.dataset.seen === "1") return;

          const hasEnoughVisibleArea =
            entry.intersectionRatio >= 0.25 ||
            entry.intersectionRect.height >= 240;

          if (entry.isIntersecting && hasEnoughVisibleArea) {
            if (!visibilityTimers.has(postId)) {
              visibilityTimers.set(
                postId,
                window.setTimeout(() => {
                  visibilityTimers.delete(postId);
                  entry.target.dataset.seen = "1";
                  queueImpression(postId);
                }, 850)
              );
            }
          } else if (visibilityTimers.has(postId)) {
            window.clearTimeout(visibilityTimers.get(postId));
            visibilityTimers.delete(postId);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -8% 0px",
        threshold: [0, 0.15, 0.25, 0.5]
      }
    );

    container
      .querySelectorAll("[data-feed-post-id]")
      .forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
      visibilityTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [posts]);

  useEffect(() => () => {
    if (impressionFlushTimerRef.current) {
      window.clearTimeout(impressionFlushTimerRef.current);
    }

    const postIds = Array.from(impressionQueueRef.current);
    if (postIds.length > 0) {
      post("posts/impressions", { postIds }).catch(() => {});
    }
  }, []);


  const resetModal = () => {
    setModalType(null);

    setLegend("");
    setLinkLegend("");
    setLinkUrl("");

    setImagePreview(null);
    setVideoPreview(null);
    setVideoFile(null);
    setErrorVideo("");
    setModalError("");

    setCrop({ x: 0, y: 0 });
    setZoom(1.2);
    setCroppedAreaPixels(null);
  };


  const handlePostImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setModalError("");
  };


  const addPost = async () => {
    if (!newPost.trim()) return;

    await post("posts", {
      id_user,
      type: "text",
      text: newPost
    });

    setNewPost("");
    fetchPosts();
  };


  const handlePublish = async () => {

    if (modalType === "post" && !imagePreview) {
      setModalError(t("feed.imageRequired"));
      return;
    }

    if (modalType === "link" && !linkUrl.trim()) {
      setModalError(t("feed.linkRequired"));
      return;
    }

    try {

      setPublishing(
        true
      );

      if (modalType === "post") {

        const croppedBlob =
          await getCroppedImg(
            imagePreview,
            croppedAreaPixels
          );

        const formData =
          new FormData();

        formData.append(
          "id_user",
          id_user
        );

        formData.append(
          "type",
          "image"
        );

        formData.append(
          "text",
          legend
        );

        formData.append(
          "file",
          croppedBlob,
          "post.jpg"
        );

        const data = await post("posts", formData);

        setPosts(prev => ([

          data.data,

          ...prev

        ]));

      }

      if (modalType === "link") {

        const res =
          await post(
            "posts",
            {
              id_user,
              type: "link",
              text: linkLegend,
              link_url: linkUrl
            }
          );

        setPosts(prev => ([
          res.data,
          ...prev
        ]));
      }


      if (modalType === "video") {

        const formData =
          new FormData();

        formData.append(
          "id_user",
          id_user
        );

        formData.append(
          "type",
          "video"
        );

        formData.append(
          "text",
          legend
        );

        formData.append(
          "file",
          videoFile
        );

        const data = await post("posts", formData);

        setPosts(prev => ([

          data.data,

          ...prev

        ]));

      }

      resetModal();

      setPublishSuccess(
        true
      );

      setTimeout(() => {

        setPublishSuccess(
          false
        );

      }, 2500);

    }
    catch (err) {

      console.log(err);

    }
    finally {

      setPublishing(
        false
      );

    }

  };


  return (
    <div className="feed" onTouchStart={handlePullStart} onTouchMove={handlePullMove} onTouchEnd={handlePullEnd}>
      <div className={`native-pull-refresh ${pullRefreshing ? "refreshing" : ""}`} style={{ "--pull-distance": `${pullDistance}px` }}>
        <span />
      </div>
      <div className="feed-layout">
        <aside className="feed-sidebar feed-sidebar-left">
          <section className="feed-side-panel feed-career-pulse-panel">
            <div className="feed-pulse-heading">
              <span className="feed-pulse-icon"><FaChartLine /></span>
              <div>
                <p className="feed-side-kicker">{t("feed.careerPulse")}</p>
                <small>{t("feed.thisWeek")}</small>
              </div>
            </div>

            <div className="feed-pulse-metric">
              <span className="feed-pulse-metric-icon"><FaRegEye /></span>
              <div>
                <small>{t("feed.profileVisibility")}</small>
                <strong>
                  {weeklyProfileViews === null
                    ? t("feed.visibilityPending")
                    : t("feed.uniqueProfileViewsWeek", { count: weeklyProfileViews })}
                </strong>
              </div>
            </div>

            <div className="feed-pulse-metric">
              <span className="feed-pulse-metric-icon"><FaUserPlus /></span>
              <div>
                <small>{t("feed.networkActivity")}</small>
                <strong>{t("feed.profilesToDiscover", { count: suggestions.length })}</strong>
              </div>
            </div>

            <div className="feed-pulse-metric feed-pulse-opportunity">
              <span className="feed-pulse-metric-icon"><FaCompass /></span>
              <div>
                <small>{t("feed.nextOpportunity")}</small>
                <strong>{suggestions[0]?.name || t("feed.matchingSuggestion")}</strong>
                {suggestions[0]?.role && <em>{getRoleLabel(suggestions[0].role)}</em>}
              </div>
            </div>

            <div className="feed-pulse-progress">
              <div className="feed-pulse-progress-label">
                <span>{t("feed.profileProgress")}</span>
                <strong>{profilePulse}%</strong>
              </div>
              <span className="feed-pulse-progress-track" aria-hidden="true">
                <span style={{ width: `${profilePulse}%` }} />
              </span>
            </div>
          </section>
        </aside>

        <div className="feed-stream" ref={feedStreamRef}>
      <div className="feed-composer">
        <div className="create-post">
          {preview === null && loading ? (
            <div className="skeleton avatar"></div>
          ) : preview ? (
            <img
              src={preview}
              className="post-user-avatar"
            />
          ) : (
            <FaUser className="post-user-icon" />
          )}
          <textarea
            placeholder={t("feed.expressYourself")}
            value={newPost}
            onChange={(e) => setNewPost(e.target.value)}
          />
          <button onClick={addPost}> {t("feed.publish")} </button>
        </div>

        <div className="post-options">
          <button onClick={() => setModalType("post")}>
            <FaCamera /> {t("feed.post")}
          </button>

          <button onClick={() => setModalType("video")}>
            <FaVideo /> {t("feed.video")}
          </button>

          <button onClick={() => setModalType("link")}>
            <FaLink /> {t("feed.link")}
          </button>

          {roleoffer ===
            "club" && (

              <button
                onClick={() =>
                  setModalType("offer")
                }
              >
                <FaBriefcase /> {t("feed.offer")}
              </button>

            )}
        </div>
      </div>

      <div className="feed-mobile-sections">
        <section className="feed-mobile-role">
          <FaBriefcase className="feed-role-icon" />
          {roleoffer === "club" ? (
            <>
              <div>
                <h3>{t("feed.newOffers")}</h3>
                <p>{t("feed.offerPanelDescription")}</p>
              </div>
              <button onClick={() => setModalType("offer")}>
                {t("feed.publish")} <FaArrowRight />
              </button>
            </>
          ) : (
            <>
              <div>
                <h3>{t("feed.recommendedProfiles")}</h3>
                <p>{t("feed.recommendedProfilesDescription")}</p>
              </div>
              <button onClick={() => onNavigate?.("matching")}>
                {t("feed.view")} <FaArrowRight />
              </button>
            </>
          )}
        </section>
      </div>

      <section className="native-feed-welcome">
        <small>CarrerUp Feed</small>
        <strong>{t("feed.welcome", { name: user?.name || user?.username || "CarrerUp" })}</strong>
      </section>


      {loading && posts.length === 0 &&
        Array.from({ length: 4 }).map((_, i) => (
          <SkeletonPost key={i} />
        ))
      }

      {posts.map((postItem, index) => (
          <div
            key={postItem.id_post}
            data-feed-post-id={postItem.id_post}
            data-seen={Number(postItem.isSeen) === 1 ? "1" : "0"}
          >

            <Post
              post={postItem}
              id_user={id_user}
              fetchPosts={fetchPosts}
              priority={index < 2}
            />

            {(index + 1) % 10 === 0 &&
              suggestions[index % suggestions.length] && (
                <SuggestedProfileCard
                  profile={suggestions[index % suggestions.length]}
                />
              )}
          </div>
        ))}

      <div ref={feedEndRef} className="feed-infinite-sentinel" aria-hidden="true" />

      {loadingMore &&
        <SkeletonPost key="loading-next-page" />
      }
        </div>

        <aside className="feed-sidebar feed-sidebar-right">
          <section className="feed-side-panel feed-suggestions-panel">
            <p className="feed-side-kicker">{t("feed.suggestedProfiles")}</p>
            {suggestions.slice(0, 3).map((profile) => (
              <SuggestedProfileCard key={profile.id} profile={profile} />
            ))}
          </section>

          <section className="feed-side-panel feed-role-panel">
            <FaBriefcase className="feed-role-icon" />
            {roleoffer === "club" ? (
              <>
                <h3>{t("feed.newOffers")}</h3>
                <p>{t("feed.offerPanelDescription")}</p>
                <button onClick={() => setModalType("offer")}>{t("feed.publishOffer")} <FaArrowRight /></button>
              </>
            ) : (
              <>
                <h3>{t("feed.developNetwork")}</h3>
                <p>{t("feed.networkPanelDescription")}</p>
                <button onClick={() => onNavigate?.("matching")}>{t("feed.viewMatching")} <FaArrowRight /></button>
              </>
            )}
          </section>
        </aside>
      </div>

      {/* MODAL */}
      {modalType && (
        <div className="modal-overlay" onClick={resetModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="feed-modal-header">
              <span className="feed-modal-icon">
                {modalType === "post" && <FaImage />}
                {modalType === "video" && <FaVideo />}
                {modalType === "link" && <FaLink />}
                {modalType === "text" && <FaFileAlt />}
              </span>
              <div>
                <h3>
                  {modalType === "post" && t("feed.createPost")}
                  {modalType === "video" && t("feed.createVideo")}
                  {modalType === "link" && t("feed.shareLink")}
                  {modalType === "text" && t("feed.createPost")}
                </h3>
                <p>
                  {modalType === "post" && t("feed.modalPostSubtitle")}
                  {modalType === "video" && t("feed.modalVideoSubtitle")}
                  {modalType === "link" && t("feed.modalLinkSubtitle")}
                  {modalType === "text" && t("feed.expressYourself")}
                </p>
              </div>
              <button className="feed-modal-close" type="button" onClick={resetModal} aria-label={t("feed.cancel")}>
                <FaTimes />
              </button>
            </div>

            {modalType === "post" && (
              <div className="feed-modal-fields">
                <textarea
                  className="feed-caption-textarea"
                  placeholder={t("feed.addCaption")}
                  value={legend}
                  onChange={(e) => setLegend(e.target.value)}
                />

                <label className={`image-upload feed-modal-upload ${imagePreview ? "has-media" : ""}`}>
                  {imagePreview ? (
                      <div className="feed-modal-cropper">
                        <Cropper
                          image={imagePreview}
                          crop={crop}
                          zoom={zoom}
                          minZoom={1}
                          maxZoom={3}
                          aspect={1}
                          restrictPosition={true}
                          objectFit="cover"
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={(croppedArea, croppedPixels) =>
                            setCroppedAreaPixels(croppedPixels)
                          }
                        />
                      </div>
                  ) : (
                    <div className="image-placeholder">
                      <span className="camera"><FaCloudUploadAlt /></span>
                      <strong>{t("feed.addImage")}</strong>
                      <small>{t("feed.imageRequired")}</small>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handlePostImage}
                    required
                  />
                </label>
              </div>
            )}

            {modalType === "text" && (
              <div className="feed-modal-fields">
                <textarea
                  className="feed-caption-textarea native-text-composer"
                  autoFocus
                  placeholder={t("feed.expressYourself")}
                  value={newPost}
                  onChange={(event) => setNewPost(event.target.value)}
                />
              </div>
            )}

            {modalType === "link" && (
              <div className="feed-modal-fields">
                <input
                  type="text"
                  placeholder={t("feed.linkCaption")}
                  value={linkLegend}
                  onChange={(e) => setLinkLegend(e.target.value)}
                />

                <input
                  type="url"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => {
                    setLinkUrl(e.target.value);
                    setModalError("");
                  }}
                  required
                />
                <p className="feed-modal-hint">{t("feed.modalLinkHint")}</p>
              </div>
            )}

            {modalType === "video" && (
              <div className="feed-modal-fields">
                <textarea
                  className="feed-caption-textarea"
                  placeholder={t("feed.addCaption")}
                  value={legend}
                  onChange={(e) => setLegend(e.target.value)}
                />

                {
                  errorVideo
                    ?
                    <div className="error-login-message error-video">
                      {errorVideo}
                    </div> : ""
                }

                <label className={`image-upload feed-modal-upload ${videoPreview ? "has-media" : ""}`}>

                  {videoPreview ? (
                    <video
                      src={videoPreview}
                      controls
                    />
                  ) : (
                    <div className="image-placeholder">
                      <FaCloudUploadAlt />
                      <strong>{t("feed.addVideo")}</strong>
                      <small>MP4, MOV · 60 sec max</small>
                    </div>
                  )}

                  <input
                    type="file"
                    accept="video/*"
                    hidden
                    onChange={handleVideoUpload}
                  />

                </label>
              </div>
            )}

            {modalError && <p className="feed-modal-error">{modalError}</p>}

            <div className="modal-actions">
              <button className="cancel" onClick={resetModal}>
                {t("feed.cancel")}
              </button>
              <button className="publish" onClick={modalType === "text" ? async () => { await addPost(); resetModal(); } : handlePublish}>
                <FaPaperPlane /> {t("feed.publish")}
              </button>
            </div>

          </div>
        </div>
      )}

      <OfferModal
        open={modalType === "offer"}
        onClose={resetModal}
      />

      {publishing && (

        <div className="publish-loading-bar">

          <div
            className="publish-progress"
          />

          <span>
            {t("feed.publishing")}
          </span>

        </div>

      )}

      {publishSuccess && (
        <div className="publish-toast">
          <div
            className="publish-success-icon"
          >
            ✓
          </div>
          <div>
            <strong>
              {t("feed.success")}
            </strong>
            <p>
              {t("feed.published")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
